// AdminWrapper.tsx
// Place in: src/components/AdminWrapper.tsx
// This controls who can access the admin panel

import React from 'react';
import AdminNotificationPanel from './AdminNotificationPanel';
import { useLanguage } from './LanguageContext';

interface AdminWrapperProps {
  isAdmin: boolean;
  apiUrl: string;
  investors?: { id: string; name: string }[];
  onClose: () => void;
}

const AdminWrapper: React.FC<AdminWrapperProps> = ({
  isAdmin,
  apiUrl,
  investors = [],
  onClose
}) => {
  const { t } = useLanguage();

  // Not authorized - show access denied
  if (!isAdmin) {
    return (
      <div style={styles.accessDenied}>
        <div style={styles.accessDeniedBox}>
          <span style={styles.lockIcon}>🔒</span>
          <h2 style={styles.title}>{t('awAccessDenied')}</h2>
          <p style={styles.message}>
            {t('awNoPermission')}
          </p>
          <button onClick={onClose} style={styles.button}>
            {t('awGoBack')}
          </button>
        </div>
      </div>
    );
  }

  // Authorized - show admin panel
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button onClick={onClose} style={styles.closeButton}>
          ← {t('awBackToPortal')}
        </button>
        <span style={styles.adminBadge}>
          {t('awAdminPanel')}
        </span>
      </div>
      <div style={styles.content}>
        <AdminNotificationPanel 
          apiUrl={apiUrl} 
          investors={investors}
        />
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: '#f5f5f5',
    zIndex: 1000,
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 20px',
    background: '#1a1a1a',
    color: 'white',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  closeButton: {
    padding: '8px 16px',
    background: 'transparent',
    border: '1px solid #D6B160',
    color: '#D6B160',
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 14,
    fontFamily: 'inherit',
  },
  adminBadge: {
    fontSize: 12,
    color: '#D6B160',
    background: 'rgba(214, 177, 96, 0.1)',
    padding: '6px 12px',
    borderRadius: 20,
  },
  content: {
    padding: '20px 0',
  },
  accessDenied: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  accessDeniedBox: {
    background: 'white',
    padding: 40,
    borderRadius: 16,
    textAlign: 'center' as const,
    maxWidth: 400,
    margin: 20,
  },
  lockIcon: {
    fontSize: 48,
  },
  title: {
    margin: '16px 0 8px',
    fontSize: 20,
    fontWeight: 700,
  },
  message: {
    color: '#666',
    marginBottom: 24,
  },
  button: {
    padding: '12px 32px',
    background: '#D6B160',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: 14,
  },
};

export default AdminWrapper;